import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ScreenLockService } from '../../../core/services/screen-lock.service';
import { ScreenLockStore } from './screen-lock.store';
import { VirtualNumberKeyboardComponent } from './virtual-number-keyboard/virtual-number-keyboard.component';

/**
 * Subtrees made `inert` while locked. The CDK overlay container is a sibling of `main`, so an already-open
 * dialog would otherwise stay in the tab order. See SCREEN_LOCK_CONTEXT.md.
 */
const INERT_CONTENT_SELECTORS: readonly string[] = ['main', '.cdk-overlay-container'];

/**
 * Full screen lock layer. A locked screen is frozen: every interaction with the board underneath is swallowed,
 * including scrolling, and any touch or click opens the numeric keypad instead. Reaching content below the fold
 * costs the PIN, same as everything else. See SCREEN_LOCK_CONTEXT.md.
 */
@Component({
  imports: [MatIconModule, TranslateModule, VirtualNumberKeyboardComponent],
  providers: [ScreenLockStore],
  selector: 'app-screen-lock',
  standalone: true,
  styleUrl: './screen-lock.component.scss',
  templateUrl: './screen-lock.component.html',
})
export class ScreenLockComponent implements AfterViewInit, OnDestroy {
  @ViewChild('overlay') private overlay!: ElementRef<HTMLElement>;

  /** Selectors of the subtrees to make `inert` while locked. Override only if the app shell changes. */
  public contentSelectors = input<readonly string[]>(INERT_CONTENT_SELECTORS);

  private readonly document = inject(DOCUMENT);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly screenLockService = inject(ScreenLockService);
  private readonly store = inject(ScreenLockStore);

  public readonly isKeypadOpen = this.screenLockService.isKeypadOpen;
  public readonly isLocked = this.screenLockService.isLocked;
  public readonly hasError = this.store.isWrongPin;
  public readonly isVerifying = this.store.isVerifying;
  /** Heading for the keypad: the board or slideshow the operator is standing in front of. */
  public readonly lockedScreenName = computed<string>(() => this.screenLockService.target()?.entityName ?? '');
  /** Where the operator last tapped to open the keypad; the keypad opens there instead of a saved position. */
  public readonly keypadOpenPoint = signal<{ x: number; y: number } | null>(null);

  /**
   * The pointer whose gesture this overlay owns, so a release can only toggle the keypad if the press that
   * started it landed here. Paired with `setPointerCapture`, which keeps the release on the overlay even if
   * the finger travels over the keypad layer — without it a drag off the overlay would leave this set and the
   * next press would be ignored.
   */
  private activePointerId: number | null = null;

  constructor() {
    effect(() => this.applyInertToContent(this.isLocked()));
  }

  public ngAfterViewInit(): void {
    const element: HTMLElement = this.overlay.nativeElement;

    // Attached outside the zone, NOT in the template: `touchmove` fires every frame of a swipe and template
    // bindings would run change detection each time. See SCREEN_LOCK_CONTEXT.md.
    this.ngZone.runOutsideAngular(() => {
      element.addEventListener('pointerdown', this.onPointerDown);
      element.addEventListener('pointerup', this.onPointerUp);
      element.addEventListener('pointercancel', this.onPointerCancel);
      element.addEventListener('contextmenu', this.onContextMenu);
      element.addEventListener('wheel', this.onWheel, { passive: false });
      element.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.document.addEventListener('keydown', this.onDocumentKeydown, { capture: true });
    });
  }

  public ngOnDestroy(): void {
    const element: HTMLElement | undefined = this.overlay?.nativeElement;

    element?.removeEventListener('pointerdown', this.onPointerDown);
    element?.removeEventListener('pointerup', this.onPointerUp);
    element?.removeEventListener('pointercancel', this.onPointerCancel);
    element?.removeEventListener('contextmenu', this.onContextMenu);
    element?.removeEventListener('wheel', this.onWheel);
    element?.removeEventListener('touchmove', this.onTouchMove);
    this.document.removeEventListener('keydown', this.onDocumentKeydown, { capture: true });

    this.applyInertToContent(false);
  }

  /** The API decides; a rejected or failed request leaves the screen locked rather than failing open. */
  public onPinSubmitted(pin: string): void {
    this.store.verifyPin(pin);
  }

  public onKeypadClosed(): void {
    this.store.clearError();
    this.screenLockService.closeKeypad();
  }

  /**
   * Escape hatch for an operator who cannot unlock: leave the guarded screen for home. Not an unlock — the
   * board stays PIN-protected on the API. `stop()` lifts the overlay and `inert` at once.
   */
  public onGoHome(): void {
    this.screenLockService.stop();
    this.router.navigateByUrl('/');
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (!this.isLocked() || this.activePointerId !== null) {
      return;
    }

    this.activePointerId = event.pointerId;
    this.overlay.nativeElement.setPointerCapture(event.pointerId);
  };

  /**
   * Any release is a tap, because a locked screen has no gesture that is not one: swiping to scroll does
   * nothing, so a swipe opens the keypad too — which is the answer the operator needs.
   */
  private readonly onPointerUp = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) {
      return;
    }

    this.releasePointer(event.pointerId);

    this.ngZone.run(() => {
      if (this.isKeypadOpen()) {
        this.onKeypadClosed();
      } else {
        this.keypadOpenPoint.set({ x: event.clientX, y: event.clientY });
        this.screenLockService.openKeypad();
      }
    });
  };

  private readonly onPointerCancel = (event: PointerEvent): void => {
    if (event.pointerId === this.activePointerId) {
      this.releasePointer(event.pointerId);
    }
  };

  private readonly onContextMenu = (event: Event): void => {
    event.preventDefault();
  };

  /**
   * A locked screen does not scroll, so the wheel is cancelled rather than forwarded. The overlay is not a
   * scroll container, so without this the wheel would scroll the document behind it. Deleting this handler
   * re-opens scrolling for every desktop mouse.
   */
  private readonly onWheel = (event: WheelEvent): void => {
    if (!this.isLocked()) {
      return;
    }

    event.preventDefault();
  };

  /**
   * Same as {@link onWheel} for touch: cancels the browser's native pan and rubber-band so a swipe cannot move
   * the board. `touch-action: none` on the overlay is the first line of defence; this is the one that holds
   * when a browser honours it loosely. Registered `{ passive: false }` or `preventDefault` is ignored.
   */
  private readonly onTouchMove = (event: TouchEvent): void => {
    if (!this.isLocked()) {
      return;
    }

    event.preventDefault();
  };

  /**
   * Blocks document-level hotkeys while locked (unless the keypad is up) — which is also what stops the arrow
   * keys, Space and PageDown from scrolling the board. Modifier combos pass through so kiosk browser shortcuts
   * (reload, fullscreen) keep working.
   */
  private readonly onDocumentKeydown = (event: KeyboardEvent): void => {
    if (!this.isLocked() || this.isKeypadOpen()) {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  private releasePointer(pointerId: number): void {
    if (this.overlay.nativeElement.hasPointerCapture(pointerId)) {
      this.overlay.nativeElement.releasePointerCapture(pointerId);
    }

    this.activePointerId = null;
  }

  /**
   * `inert` makes the board read-only: it drops the subtree out of the focus order so Tab cannot reach it. It
   * does NOT evict focus already inside, so the focused element is blurred by hand — otherwise it keeps
   * receiving keystrokes. See SCREEN_LOCK_CONTEXT.md.
   */
  private applyInertToContent(isInert: boolean): void {
    for (const selector of this.contentSelectors()) {
      this.document.querySelectorAll<HTMLElement>(selector).forEach((element: HTMLElement) => {
        element.inert = isInert;
      });
    }

    if (!isInert) {
      return;
    }

    const { activeElement } = this.document;

    if (activeElement instanceof HTMLElement && !this.elementRef.nativeElement.contains(activeElement)) {
      activeElement.blur();
    }
  }
}
